import * as DocumentPicker from 'expo-document-picker';
import React, { useState } from 'react';
import { StyleSheet, TouchableOpacity, View } from 'react-native';
import { Colors } from '../constants/Colors';
import { useDynamicStyles } from '../hooks/useDynamicStyles';
import type { WithResponsive } from '../utils/responsivity';
import { Button, ButtonVariants } from './Button';
import { TextBold, TextReg } from './StyledText';

export type FileResult = {
  uri: string;
  name: string;
  size?: number;
  mimeType?: string;
};

type Props = {
  label: string;
  showLabel?: boolean;
  value?: FileResult | null;
  onChange?: (file: FileResult | null) => void;
  onError?: (error: string) => void;
  placeholder?: string;
  disabled?: boolean;
  maxSizeBytes?: number; // Default: 10MB
  acceptedTypes?: string[]; // e.g., ['application/pdf', 'image/*']
  error?: boolean;
  helperText?: string;
};

export const FileInput: React.FC<Props> = ({
  label,
  showLabel = true,
  value,
  onChange,
  onError,
  placeholder = 'Choose a file',
  disabled = false,
  maxSizeBytes = 10 * 1024 * 1024, // 10MB default
  acceptedTypes,
  error,
  helperText,
}) => {
  const styles = useDynamicStyles(createStyles);
  const [isSelecting, setIsSelecting] = useState(false);

  const formatFileSize = (bytes?: number): string => {
    if (!bytes) return 'Unknown size';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const handlePickFile = async () => {
    if (disabled || isSelecting) return;

    try {
      setIsSelecting(true);

      const result = await DocumentPicker.getDocumentAsync({
        type: acceptedTypes || '*/*',
        copyToCacheDirectory: true,
        multiple: false,
      });

      if (result.canceled) {
        setIsSelecting(false);
        return;
      }

      const file = result.assets[0];

      // Validate file size
      if (file.size && file.size > maxSizeBytes) {
        const fileSizeMB = (file.size / (1024 * 1024)).toFixed(1);
        const maxSizeMB = (maxSizeBytes / (1024 * 1024)).toFixed(1);
        const errorMsg = `File size (${fileSizeMB}MB) exceeds the ${maxSizeMB}MB limit`;
        onError?.(errorMsg);
        setIsSelecting(false);
        return;
      }

      // Create file result
      const fileResult: FileResult = {
        uri: file.uri,
        name: file.name,
        size: file.size,
        mimeType: file.mimeType,
      };

      onChange?.(fileResult);
      setIsSelecting(false);
    } catch (err) {
      console.error('Error picking file:', err);
      const errorMsg = 'Failed to pick file. Please try again.';
      onError?.(errorMsg);
      setIsSelecting(false);
    }
  };

  const handleClearFile = () => {
    if (disabled) return;
    onChange?.(null);
  };

  return (
    <View style={styles.container}>
      {showLabel && <TextBold style={styles.label}>{label}</TextBold>}

      <View style={[styles.inputWrapper, error && styles.errorBorder]}>
        {value ? (
          // Show selected file
          <View style={styles.fileInfo}>
            <View style={styles.fileDetails}>
              <TextBold style={styles.fileName} numberOfLines={1}>
                {value.name}
              </TextBold>
              <TextReg style={styles.fileSize}>
                {formatFileSize(value.size)}
              </TextReg>
            </View>
            <TouchableOpacity
              onPress={handleClearFile}
              disabled={disabled}
              style={styles.clearButton}
            >
              <TextReg style={styles.clearButtonText}>×</TextReg>
            </TouchableOpacity>
          </View>
        ) : (
          // Show file picker button
          <Button
            variant={ButtonVariants.WHITE}
            onPress={handlePickFile}
            disabled={disabled || isSelecting}
            style={styles.pickButton}
          >
            {isSelecting ? 'Selecting...' : placeholder}
          </Button>
        )}
      </View>

      {helperText && (
        <TextReg style={[styles.helperText, error && styles.errorText]}>
          {helperText}
        </TextReg>
      )}
    </View>
  );
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const createStyles = (_: WithResponsive) =>
  StyleSheet.create({
    container: {
      gap: 8,
    },
    label: {
      marginBottom: 4,
      color: Colors.text,
      fontSize: 16,
    },
    inputWrapper: {
      borderRadius: 30,
      borderWidth: 1,
      borderColor: Colors.opacity20,
      backgroundColor: Colors.opacity05,
      minHeight: 56,
      justifyContent: 'center',
      paddingHorizontal: 16,
    },
    errorBorder: {
      // borderColor: Colors.pink0,
      // borderWidth: 1,
    },
    fileInfo: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 12,
    },
    fileDetails: {
      flex: 1,
      gap: 4,
    },
    fileName: {
      color: Colors.text,
      fontSize: 16,
    },
    fileSize: {
      color: Colors.opacity50,
      fontSize: 14,
    },
    clearButton: {
      width: 32,
      height: 32,
      borderRadius: 16,
      backgroundColor: Colors.opacity10,
      alignItems: 'center',
      justifyContent: 'center',
    },
    clearButtonText: {
      color: Colors.text,
      fontSize: 24,
      lineHeight: 24,
    },
    pickButton: {
      borderRadius: 30,
    },
    helperText: {
      color: Colors.opacity50,
      fontSize: 12,
      marginLeft: 16,
    },
    errorText: {
      color: Colors.pink0,
    },
  });
